<script setup lang="ts">
import AppButton from "@/components/atoms/button/AppButton.vue";
import { VUE_APP_ENV } from "@/config/VueAppEnv";
import ExternalLink from "@/const/externalLink";
import { authStore } from "@/store/auth";
import { computed, onMounted } from "vue";
import { AbTest3113 } from "@/app/services/AbTestService";
import { sendAdjustEvent } from "@/plugins/adjust";
import { useCustomRouter } from "@/utils/router";
import { deviceModule } from "@/store/device";

const { pushIgnoreError } = useCustomRouter();

const canSkipRegistration = computed(
  () => authStore.isCaitechPharmacistProduct && deviceModule.isIos
);
const productAssetSuffix = computed(() => authStore.productAssetSuffix);
const ruleLink = computed(() =>
  authStore.isCaitechPharmacistProduct
    ? ExternalLink.RULE_CAITECH_PHARMACIST
    : ExternalLink.RULE
);
const privacyLink = computed(() => ExternalLink.PRIVACY);
const registerOrLoginRoute = computed(() => ({ name: "RegisterOrLogin" }));
const isDevMenuVisible = computed(() => {
  const env = VUE_APP_ENV;
  return !(env === "prod" || env === "demo");
});
const testGroup = computed(() => AbTest3113.getTestGroup());
const isAbTest3113GroupAorB = computed(() =>
  ["A", "B"].includes(testGroup.value)
);
const backgroundClass = computed(() =>
  !isAbTest3113GroupAorB.value && testGroup.value
    ? `background-without-text-${testGroup.value.toLowerCase()}`
    : null
);

onMounted(() => {
  AbTest3113.registerEvent(AbTest3113.eventTypes.entry);
});

function moveToSearch() {
  sendAdjustEvent("SKIP_REGISTRATION");
  pushIgnoreError({ name: "Search" });
}

function changeProduct(productSlug: string) {
  authStore.changeProduct(productSlug);
  location.reload();
}
</script>

<template>
  <!-- グループA,Bの場合は既存のレイアウト -->
  <div v-if="isAbTest3113GroupAorB" class="bg-white vh-100 d-flex flex-column">
    <div class="notch"></div>
    <div
      class="pt-3 mb-4 d-flex align-items-center justify-content-center"
      :class="`background-without-text${productAssetSuffix}`"
    />
    <div class="flex-grow-1 d-flex flex-column justify-content-end">
      <div v-if="isDevMenuVisible" class="text-center m-3 p-2 small border">
        <p class="mb-1">開発メニュー(prod, demoでは表示されません)</p>
        <AppButton
          block
          size="sm"
          variant="mild"
          @click="changeProduct('caitech_care')"
        >
          「カイテク（介護・看護）」アプリに変更
        </AppButton>
        <AppButton
          block
          size="sm"
          variant="mild"
          @click="changeProduct('caitech_pharmacist')"
        >
          「カイテク薬剤師」アプリに変更
        </AppButton>
      </div>

      <div class="link-and-button-container">
        <p class="text-center mt-3 small">
          <a :href="ruleLink" class="text-primary">利用規約</a>と<a
            :href="privacyLink"
            class="text-primary"
            >プライバシーポリシー</a
          >に同意して
        </p>

        <div class="button-container">
          <AppButton
            block
            class="rounded start-button"
            :class="{ 'with-skip-link': canSkipRegistration }"
            :to="registerOrLoginRoute"
          >
            はじめる
          </AppButton>

          <div class="skip-link-container" v-if="canSkipRegistration">
            <u class="text-size-14 text-mild" @click="moveToSearch">
              スキップしてお仕事を見てみる
            </u>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- グループC,D,E,Fの場合は新しいレイアウト -->
  <div v-else class="main-container">
    <div class="notch"></div>
    <div class="welcome-section">
      <div class="background-image" :class="backgroundClass"></div>
    </div>
    <div class="bottom-section">
      <div v-if="isDevMenuVisible" class="text-center m-3 p-2 small border">
        <p class="mb-1">開発メニュー(prod, demoでは表示されません)</p>
        <AppButton
          block
          size="sm"
          variant="mild"
          @click="changeProduct('caitech_care')"
        >
          「カイテク（介護・看護）」アプリに変更
        </AppButton>
        <AppButton
          block
          size="sm"
          variant="mild"
          @click="changeProduct('caitech_pharmacist')"
        >
          「カイテク薬剤師」アプリに変更
        </AppButton>
      </div>

      <div class="link-and-button-container">
        <p class="text-center mt-3 small">
          <a :href="ruleLink" class="text-primary">利用規約</a>と<a
            :href="privacyLink"
            class="text-primary"
            >プライバシーポリシー</a
          >に同意して
        </p>

        <div class="button-container">
          <AppButton
            block
            class="rounded start-button"
            :to="registerOrLoginRoute"
          >
            はじめる
          </AppButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.notch {
  height: env(safe-area-inset-top, 0);
}

.background-without-text {
  min-height: 390px;
  margin-top: 64px;
  background: url("@/assets/svg/Onboarding/welcome-without-text.svg") no-repeat
    top;
  background-position-y: 1rem;
}

.background-without-text-caitech_pharmacist {
  min-height: 390px;
  margin-top: 64px;
  background: url("@/assets/svg/Onboarding/welcome-caitech_pharmacist.svg")
    no-repeat top;
  background-position-y: 1rem;
}

.main-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: white;
}

.welcome-section {
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 60px 0 40px;
}

.background-image {
  width: 100%;
  height: 100%;
  max-height: 80vh;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.bottom-section {
  position: relative;
}

.background-without-text-c {
  background-image: url("@/assets/svg/Onboarding/welcome-without-text-c.svg");
  background-position: center center;
  background-size: min(80%, 450px);
}

.background-without-text-d {
  background-image: url("@/assets/svg/Onboarding/welcome-without-text-d.svg");
  background-position: center center;
  background-size: min(80%, 450px);
}

.background-without-text-e {
  background-image: url("@/assets/svg/Onboarding/welcome-without-text-e.svg");
  background-position: center center;
  background-size: min(80%, 450px);
}

.background-without-text-f {
  background-image: url("@/assets/svg/Onboarding/welcome-without-text-f.svg");
  background-position: center center;
  background-size: min(80%, 450px);
}

.main-container:has(.background-without-text-c) .background-without-text-c,
.main-container:has(.background-without-text-d) .background-without-text-d,
.main-container:has(.background-without-text-e) .background-without-text-e,
.main-container:has(.background-without-text-f) .background-without-text-f {
  background-size: contain;
}

.rounded {
  border-radius: 50px !important;
}

.link-and-button-container {
  bottom: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.button-container {
  padding: 0 2.5rem;
}

.start-button {
  box-sizing: border-box;
  max-width: 560px;
  margin: 1rem auto 0 !important;
  margin-bottom: calc(64px - 16px + env(safe-area-inset-bottom, 0)) !important;
  // iPad mini、モバイルデバイス
  @media screen and (max-width: 768px) {
    max-width: 480px;
  }

  &.with-skip-link {
    margin-bottom: 16px !important;
  }
}

.skip-link-container {
  margin-bottom: calc(48px + env(safe-area-inset-bottom, 0)) !important;
  text-align: center;
}

.background-without-text-c.background-image,
.background-without-text-d.background-image,
.background-without-text-e.background-image,
.background-without-text-f.background-image {
  height: 100%;
  max-height: none;
}

.main-container:has(.background-without-text-c),
.main-container:has(.background-without-text-d),
.main-container:has(.background-without-text-e),
.main-container:has(.background-without-text-f) {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;

  .welcome-section {
    min-height: 0;
    padding: 20px;
    overflow: hidden;
  }

  .bottom-section {
    flex-shrink: 0;
    padding: 0 20px;
    padding-bottom: max(20px, env(safe-area-inset-bottom));
  }
}
</style>
